import React, { useState, useRef } from 'react';
import PageTitle from '../elements/PageTitle';

/* ─── Data ─────────────────────────────────────────────────────────────────── */

const ACTIVITY_LEVELS = [
  { key: 'sedentary', label: 'Sedentary',         desc: 'Little / no exercise',        mult: 1.2,   icon: '🪑' },
  { key: 'light',     label: 'Lightly Active',    desc: '1–3 days / week',             mult: 1.375, icon: '🚶' },
  { key: 'moderate',  label: 'Moderately Active', desc: '3–5 days / week',             mult: 1.55,  icon: '🏃' },
  { key: 'very',      label: 'Very Active',       desc: '6–7 days / week',             mult: 1.725, icon: '⚡' },
  { key: 'extra',     label: 'Extra Active',      desc: 'Athlete / 2× daily',          mult: 1.9,   icon: '🏆' },
];

const GOALS = [
  { key: 'fat_loss',    label: 'Fat Loss',    desc: '−20% calorie deficit',   emoji: '🔥', color: '#ef4444', adjFn: t => Math.round(t * 0.8),  pFact: 2.2, fatPct: 0.25 },
  { key: 'maintain',   label: 'Maintenance', desc: 'Sustain current weight',  emoji: '⚖️', color: '#f97316', adjFn: t => Math.round(t),         pFact: 2.0, fatPct: 0.25 },
  { key: 'muscle',     label: 'Muscle Gain', desc: '+300 calorie surplus',    emoji: '💪', color: '#22c55e', adjFn: t => Math.round(t + 300),   pFact: 2.5, fatPct: 0.20 },
];

const BMI_CATS = [
  { range: 'Below 18.5', label: 'Underweight', color: '#3b82f6', min: 0,    max: 18.5 },
  { range: '18.5 – 24.9', label: 'Normal',    color: '#22c55e', min: 18.5, max: 25   },
  { range: '25.0 – 29.9', label: 'Overweight', color: '#f97316', min: 25,   max: 30   },
  { range: '30.0+',        label: 'Obese',      color: '#ef4444', min: 30,   max: Infinity },
];

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

function bmiCat(bmi) { return BMI_CATS.find(c => bmi >= c.min && bmi < c.max); }

function calcAll({ gender, height, weight, age, activity, goal }) {
  const h = parseFloat(height), w = parseFloat(weight), a = parseInt(age, 10);
  if (!h || !w || !a || h <= 0 || w <= 0 || a <= 0) return null;

  const bmi   = w / ((h / 100) ** 2);
  const cat   = bmiCat(bmi);
  const bmr   = gender === 'female'
    ? 10 * w + 6.25 * h - 5 * a - 161
    : 10 * w + 6.25 * h - 5 * a + 5;

  const actObj  = ACTIVITY_LEVELS.find(x => x.key === activity) || ACTIVITY_LEVELS[2];
  const goalObj = GOALS.find(x => x.key === goal) || GOALS[1];

  const tdee     = bmr * actObj.mult;
  const calories = goalObj.adjFn(tdee);

  const proteinG = Math.round(goalObj.pFact * w);
  const fatG     = Math.round((calories * goalObj.fatPct) / 9);
  const carbG    = Math.max(0, Math.round((calories - proteinG * 4 - fatG * 9) / 4));

  const pPct = Math.round((proteinG * 4 / calories) * 100);
  const fPct = Math.round((fatG     * 9 / calories) * 100);
  const cPct = Math.max(0, 100 - pPct - fPct);

  return { bmi: bmi.toFixed(1), cat, bmr: Math.round(bmr), tdee: Math.round(tdee),
    calories, goalObj,
    macros: {
      protein: { g: proteinG, pct: pPct, color: '#ef4444', label: 'Protein' },
      carbs:   { g: carbG,    pct: cPct, color: '#f59e0b', label: 'Carbs'   },
      fat:     { g: fatG,     pct: fPct, color: '#3b82f6', label: 'Fat'     },
    },
  };
}

/* ─── Sub-components ────────────────────────────────────────────────────────── */

function NumberInput({ label, unit, value, onChange, placeholder, min = 1, max }) {
  return (
    <div className="calc-field">
      <label>{label}</label>
      <div className="calc-input-wrap">
        <input
          type="number" value={value} min={min} max={max}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
        />
        <span className="calc-unit">{unit}</span>
      </div>
    </div>
  );
}

function GenderPill({ value, onChange }) {
  return (
    <div className="calc-field">
      <label>Biological Sex</label>
      <div className="gender-pills">
        {['male', 'female'].map(g => (
          <button key={g} type="button"
            className={`gender-pill ${value === g ? 'active' : ''}`}
            onClick={() => onChange(g)}>
            <span>{g === 'male' ? '♂' : '♀'}</span> {g.charAt(0).toUpperCase() + g.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

function MacroDonut({ macros }) {
  const { protein, carbs, fat } = macros;
  const p = protein.pct, c = carbs.pct;
  const gradient = `conic-gradient(
    ${protein.color} 0% ${p}%,
    ${carbs.color} ${p}% ${p + c}%,
    ${fat.color} ${p + c}% 100%
  )`;
  return (
    <div className="donut-wrap">
      <div className="donut" style={{ background: gradient }}>
        <div className="donut-hole">
          <span className="donut-kcal-label">Total</span>
        </div>
      </div>
      <div className="macro-legend">
        {[protein, carbs, fat].map(m => (
          <div key={m.label} className="macro-legend-row">
            <span className="macro-dot" style={{ background: m.color }} />
            <span className="macro-name">{m.label}</span>
            <span className="macro-g">{m.g}g</span>
            <span className="macro-pct">({m.pct}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* gauge: maps BMI 15–40 onto 0–180 deg */
function BMIGauge({ bmi }) {
  const bmiNum  = parseFloat(bmi);
  const cat = bmiCat(bmiNum);

  // SVG semicircle gauge
  const r = 70, cx = 90, cy = 90;
  const zones = [
    { color: '#3b82f6', from: 0,   to: 18.5 },
    { color: '#22c55e', from: 18.5, to: 25  },
    { color: '#f97316', from: 25,   to: 30  },
    { color: '#ef4444', from: 30,   to: 40  },
  ];

  function toRad(bmiVal) {
    const c = Math.max(15, Math.min(40, bmiVal));
    return (((c - 15) / 25) * Math.PI); // 0 → π
  }

  function arcPath(fromBmi, toBmi, radius) {
    const a1 = toRad(fromBmi) + Math.PI; // start at left (π + offset)
    const a2 = toRad(toBmi)   + Math.PI;
    const x1 = cx + radius * Math.cos(a1);
    const y1 = cy + radius * Math.sin(a1);
    const x2 = cx + radius * Math.cos(a2);
    const y2 = cy + radius * Math.sin(a2);
    const large = (toBmi - fromBmi) / 25 > 0.5 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
  }

  const needleAngle = Math.PI + toRad(bmiNum); // pointing direction
  const nLen = 52;
  const nx = cx + nLen * Math.cos(needleAngle);
  const ny = cy + nLen * Math.sin(needleAngle);

  return (
    <div className="gauge-wrap">
      <svg viewBox="0 0 180 100" className="gauge-svg">
        {/* Track */}
        {zones.map((z, i) => (
          <path key={i}
            d={arcPath(Math.max(z.from, 15), Math.min(z.to, 40), r)}
            fill="none" stroke={z.color} strokeWidth="14" strokeLinecap="butt" />
        ))}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny}
          stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="5" fill="#fff" />
        {/* Label */}
        <text x={cx} y={cy - 10} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="800">{bmi}</text>
        <text x={cx} y={cy + 5} textAnchor="middle" fill={cat?.color} fontSize="9" fontWeight="700">{cat?.label?.toUpperCase()}</text>
      </svg>
      <div className="gauge-legend">
        {BMI_CATS.map(c => (
          <div key={c.label} className="gauge-legend-item" style={{ opacity: cat?.label === c.label ? 1 : 0.45 }}>
            <span className="gauge-dot" style={{ background: c.color }} />
            <span>{c.label}</span>
            <small>{c.range}</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricCard({ label, value, unit, sub, accent }) {
  return (
    <div className="metric-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={{ color: accent }}>{value}<span className="metric-unit"> {unit}</span></div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────────────── */

const WeightCalculator = () => {
  const [gender,   setGender]   = useState('male');
  const [height,   setHeight]   = useState('');
  const [weight,   setWeight]   = useState('');
  const [age,      setAge]      = useState('');
  const [activity, setActivity] = useState('moderate');
  const [goal,     setGoal]     = useState('maintain');
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');
  const resultsRef = useRef(null);

  const handleCalc = (e) => {
    e.preventDefault();
    setError('');
    const r = calcAll({ gender, height, weight, age, activity, goal });
    if (!r) { setError('Please fill in all fields with valid values.'); return; }
    setResult(r);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const handleReset = () => { setResult(null); setError(''); };

  return (
    <>
      <style>{`
        /* ── Layout ── */
        .calc-page { background: #0d0d0d; color: #fff; padding: 60px 0 80px; }
        .calc-page h1,.calc-page h2,.calc-page h3,.calc-page h4,.calc-page h5,.calc-page h6,
        .results-section h1,.results-section h2,.results-section h3,.results-section h4,.results-section h5,.results-section h6 { color: #fff; }
        .results-section { color: #fff; }
        .calc-intro { text-align: center; margin-bottom: 52px; }
        .calc-intro .sub { color: var(--primary,#c0392b); font-size: 11px; letter-spacing: 4px; text-transform: uppercase; font-weight: 700; }
        .calc-intro h1 { font-size: clamp(28px,6vw,52px); font-weight: 900; margin: 10px 0 14px; }
        .calc-intro p { color: #999; font-size: 15px; max-width: 500px; margin: 0 auto; }

        /* ── Steps ── */
        .calc-steps { display: grid; gap: 36px; }

        .calc-step-block { background: #161616; border-radius: 16px; padding: 30px 28px; border: 1px solid #222; }
        .calc-step-title { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color: var(--primary,#c0392b); font-weight: 700; margin-bottom: 6px; }
        .calc-step-heading { font-size: 20px; font-weight: 800; margin-bottom: 24px; }

        .calc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        @media(max-width:540px){ .calc-grid{ grid-template-columns: 1fr; } }

        .calc-field label { font-size: 12px; color: #888; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 8px; display: block; }
        .calc-input-wrap { position: relative; }
        .calc-input-wrap input {
          width: 100%; background: #0d0d0d; border: 1px solid #333;
          border-radius: 10px; color: #fff; font-size: 18px; font-weight: 700;
          padding: 14px 52px 14px 16px; outline: none; transition: border-color .2s;
        }
        .calc-input-wrap input:focus { border-color: var(--primary,#c0392b); }
        .calc-input-wrap input::placeholder { color: #444; font-weight: 400; font-size: 15px; }
        .calc-unit {
          position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
          color: #666; font-size: 12px; text-transform: uppercase; letter-spacing: 1px;
        }

        /* ── Gender pills ── */
        .gender-pills { display: flex; gap: 10px; }
        .gender-pill {
          flex: 1; padding: 12px; border-radius: 10px; border: 1.5px solid #333;
          background: #0d0d0d; color: #888; font-size: 14px; font-weight: 600;
          cursor: pointer; transition: all .2s; display: flex; align-items: center; justify-content: center; gap: 6px;
        }
        .gender-pill span { font-size: 18px; }
        .gender-pill.active { border-color: var(--primary,#c0392b); background: rgba(192,57,43,.12); color: #fff; }

        /* ── Activity cards ── */
        .activity-grid { display: grid; grid-template-columns: repeat(5,1fr); gap: 10px; }
        @media(max-width:768px){ .activity-grid{ grid-template-columns: repeat(3,1fr); } }
        @media(max-width:440px){ .activity-grid{ grid-template-columns: 1fr 1fr; } }
        .activity-card {
          background: #0d0d0d; border: 1.5px solid #2a2a2a; border-radius: 12px;
          padding: 14px 8px; cursor: pointer; text-align: center; transition: all .2s;
        }
        .activity-card:hover { border-color: #555; }
        .activity-card.active { border-color: var(--primary,#c0392b); background: rgba(192,57,43,.1); }
        .activity-card .act-icon { font-size: 22px; margin-bottom: 6px; }
        .activity-card .act-label { font-size: 11px; font-weight: 700; color: #fff; margin-bottom: 2px; }
        .activity-card .act-desc { font-size: 10px; color: #666; line-height: 1.3; }

        /* ── Goal cards ── */
        .goal-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
        @media(max-width:540px){ .goal-grid{ grid-template-columns: 1fr; } }
        .goal-card {
          border: 1.5px solid #2a2a2a; border-radius: 14px; padding: 20px 16px;
          cursor: pointer; transition: all .25s; background: #0d0d0d; text-align: center;
        }
        .goal-card:hover { border-color: #555; transform: translateY(-2px); }
        .goal-card.active { transform: translateY(-3px); }
        .goal-card .goal-emoji { font-size: 32px; margin-bottom: 10px; }
        .goal-card .goal-label { font-size: 15px; font-weight: 800; margin-bottom: 4px; }
        .goal-card .goal-desc { font-size: 11px; color: #777; }

        /* ── Calc button ── */
        .calc-btn-wrap { text-align: center; margin-top: 36px; }
        .calc-btn {
          background: var(--primary,#c0392b); color: #fff; border: none;
          padding: 18px 52px; border-radius: 12px; font-size: 15px; font-weight: 800;
          letter-spacing: 2px; text-transform: uppercase; cursor: pointer;
          transition: all .2s; box-shadow: 0 8px 24px rgba(192,57,43,.4);
        }
        .calc-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(192,57,43,.5); }
        .calc-error { color: #ef4444; text-align: center; margin-top: 14px; font-size: 13px; }

        /* ── Results ── */
        .results-section { background: #111; padding: 60px 0; border-top: 1px solid #222; }
        .results-header { text-align: center; margin-bottom: 48px; }
        .results-header .sub { color: var(--primary,#c0392b); font-size: 11px; letter-spacing: 4px; text-transform: uppercase; font-weight: 700; }
        .results-header h2 { font-size: clamp(24px,5vw,40px); font-weight: 900; color: #fff; margin-top: 8px; }

        /* metric cards */
        .metrics-row { display: grid; grid-template-columns: repeat(4,1fr); gap: 16px; margin-bottom: 48px; }
        @media(max-width:900px){ .metrics-row{ grid-template-columns: 1fr 1fr; } }
        @media(max-width:480px){ .metrics-row{ grid-template-columns: 1fr; } }

        .metric-card {
          background: #1a1a1a; border-radius: 14px; padding: 22px 20px;
          border: 1px solid #2a2a2a; animation: fadeUp .5s ease forwards;
        }
        .metric-label { font-size: 10px; color: #777; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; }
        .metric-value { font-size: 28px; font-weight: 900; line-height: 1; }
        .metric-unit { font-size: 12px; color: #666; font-weight: 400; }
        .metric-sub { font-size: 11px; color: #555; margin-top: 6px; }

        /* gauge */
        .gauge-section { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; margin-bottom: 48px; }
        @media(max-width:768px){ .gauge-section{ grid-template-columns: 1fr; } }
        .gauge-block { background: #1a1a1a; border-radius: 16px; padding: 28px 24px; border: 1px solid #2a2a2a; }
        .gauge-block-title { font-size: 11px; color: #777; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 20px; }

        .gauge-wrap { text-align: center; }
        .gauge-svg { width: 100%; max-width: 200px; }
        .gauge-legend { display: flex; flex-direction: column; gap: 6px; margin-top: 16px; text-align: left; display: inline-flex; }
        .gauge-legend-item { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #ccc; transition: opacity .3s; }
        .gauge-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .gauge-legend-item small { color: #555; font-size: 10px; }

        /* donut */
        .donut-wrap { display: flex; flex-direction: column; align-items: center; gap: 24px; }
        @media(min-width:500px){ .donut-wrap{ flex-direction: row; justify-content: center; } }
        .donut {
          width: 160px; height: 160px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          animation: fadeUp .6s ease forwards;
        }
        .donut-hole {
          width: 100px; height: 100px; background: #1a1a1a;
          border-radius: 50%; display: flex; flex-direction: column;
          align-items: center; justify-content: center;
        }
        .donut-kcal-label { font-size: 11px; color: #777; letter-spacing: 1px; }

        .macro-legend { display: flex; flex-direction: column; gap: 14px; }
        .macro-legend-row { display: grid; grid-template-columns: 10px 70px 50px 1fr; align-items: center; gap: 10px; }
        .macro-dot { width: 10px; height: 10px; border-radius: 50%; }
        .macro-name { font-size: 13px; color: #ccc; font-weight: 600; }
        .macro-g { font-size: 18px; font-weight: 800; color: #fff; }
        .macro-pct { font-size: 11px; color: #666; }

        /* macro bars */
        .macro-bars { margin-top: 28px; display: flex; flex-direction: column; gap: 14px; }
        .macro-bar-row { display: flex; flex-direction: column; gap: 6px; }
        .macro-bar-header { display: flex; justify-content: space-between; font-size: 12px; }
        .macro-bar-label { color: #ccc; font-weight: 600; }
        .macro-bar-value { color: #999; }
        .macro-bar-track { height: 8px; background: #2a2a2a; border-radius: 6px; overflow: hidden; }
        .macro-bar-fill { height: 100%; border-radius: 6px; transition: width .8s cubic-bezier(.4,0,.2,1); }

        /* bmi table */
        .bmi-table-block { background: #1a1a1a; border-radius: 16px; padding: 28px 24px; border: 1px solid #2a2a2a; }
        .bmi-table { width: 100%; border-collapse: collapse; }
        .bmi-table th { color: #777; font-size: 10px; letter-spacing: 2px; text-transform: uppercase; padding: 0 0 12px; text-align: left; }
        .bmi-table td { padding: 10px 0; border-top: 1px solid #222; font-size: 13px; color: #ccc; }
        .bmi-table tr.highlight td { font-weight: 800; color: #fff; background: rgba(255,255,255,.04); }
        .bmi-table td:first-child { padding-left: 8px; border-radius: 6px 0 0 6px; }
        .bmi-table td:last-child  { border-radius: 0 6px 6px 0; padding-right: 8px; }

        /* reset btn */
        .reset-btn {
          display: block; margin: 32px auto 0; background: transparent;
          border: 1.5px solid #333; color: #777; padding: 10px 28px;
          border-radius: 8px; font-size: 12px; letter-spacing: 2px;
          text-transform: uppercase; cursor: pointer; transition: all .2s;
        }
        .reset-btn:hover { border-color: #666; color: #ccc; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="page-content">
        <PageTitle activePage="Fitness Calculator" parentTitle="Tools" />

        {/* ══ INPUTS ══════════════════════════════════════════════════════════ */}
        <div className="calc-page">
          <div className="container">

            <div className="calc-intro">
              <div className="sub">Health &amp; Fitness Tools</div>
              <h1>Body Metrics Calculator</h1>
              <p>Calculate your BMI, BMR, daily calorie needs, and personalised macros in seconds.</p>
            </div>

            <form onSubmit={handleCalc}>
              <div className="calc-steps">

                {/* Step 1 — Your Stats */}
                <div className="calc-step-block">
                  <div className="calc-step-title">Step 1</div>
                  <div className="calc-step-heading">Your Body Stats</div>
                  <div className="calc-grid">
                    <GenderPill value={gender} onChange={setGender} />
                    <NumberInput label="Age" unit="yrs" value={age}    onChange={setAge}    placeholder="e.g. 25" min={1} max={120} />
                    <NumberInput label="Height" unit="cm" value={height} onChange={setHeight} placeholder="e.g. 175" min={50} max={250} />
                    <NumberInput label="Weight" unit="kg" value={weight} onChange={setWeight} placeholder="e.g. 70"  min={20} max={400} />
                  </div>
                </div>

                {/* Step 2 — Activity Level */}
                <div className="calc-step-block">
                  <div className="calc-step-title">Step 2</div>
                  <div className="calc-step-heading">Activity Level</div>
                  <div className="activity-grid">
                    {ACTIVITY_LEVELS.map(a => (
                      <button key={a.key} type="button"
                        className={`activity-card ${activity === a.key ? 'active' : ''}`}
                        onClick={() => setActivity(a.key)}>
                        <div className="act-icon">{a.icon}</div>
                        <div className="act-label">{a.label}</div>
                        <div className="act-desc">{a.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 3 — Goal */}
                <div className="calc-step-block">
                  <div className="calc-step-title">Step 3</div>
                  <div className="calc-step-heading">Your Goal</div>
                  <div className="goal-grid">
                    {GOALS.map(g => (
                      <button key={g.key} type="button"
                        className={`goal-card ${goal === g.key ? 'active' : ''}`}
                        style={goal === g.key ? { borderColor: g.color, background: g.color + '14' } : {}}
                        onClick={() => setGoal(g.key)}>
                        <div className="goal-emoji">{g.emoji}</div>
                        <div className="goal-label" style={goal === g.key ? { color: g.color } : {}}>{g.label}</div>
                        <div className="goal-desc">{g.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* Calculate */}
              <div className="calc-btn-wrap">
                <button type="submit" className="calc-btn">Calculate My Metrics</button>
                {error && <div className="calc-error">{error}</div>}
              </div>
            </form>
          </div>
        </div>

        {/* ══ RESULTS ═════════════════════════════════════════════════════════ */}
        {result && (
          <div className="results-section" ref={resultsRef}>
            <div className="container">

              <div className="results-header">
                <div className="sub">Your Results</div>
                <h2>Your Personalised Metrics</h2>
              </div>

              {/* 4 metric cards */}
              <div className="metrics-row">
                <MetricCard label="BMI"               value={result.bmi}      unit=""           sub={result.cat.label}            accent={result.cat.color} />
                <MetricCard label="Basal Metabolic Rate" value={result.bmr}   unit="kcal/day"   sub="Calories burned at rest"     accent="#f97316" />
                <MetricCard label="TDEE"               value={result.tdee}    unit="kcal/day"   sub="Total daily energy expenditure" accent="#3b82f6" />
                <MetricCard label={`${result.goalObj.label} Target`} value={result.calories} unit="kcal/day" sub="Goal-adjusted daily intake" accent={result.goalObj.color} />
              </div>

              {/* BMI gauge + macro donut */}
              <div className="gauge-section">

                <div className="gauge-block">
                  <div className="gauge-block-title">BMI Gauge</div>
                  <BMIGauge bmi={result.bmi} />
                </div>

                <div className="gauge-block">
                  <div className="gauge-block-title">Daily Macro Breakdown — {result.calories} kcal</div>
                  <MacroDonut macros={result.macros} />

                  {/* Macro bars */}
                  <div className="macro-bars">
                    {Object.values(result.macros).map(m => (
                      <div key={m.label} className="macro-bar-row">
                        <div className="macro-bar-header">
                          <span className="macro-bar-label">{m.label}</span>
                          <span className="macro-bar-value">{m.g}g &nbsp;·&nbsp; {m.pct}%</span>
                        </div>
                        <div className="macro-bar-track">
                          <div className="macro-bar-fill" style={{ width: `${m.pct}%`, background: m.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* BMI reference table */}
              <div className="bmi-table-block">
                <div className="gauge-block-title" style={{ marginBottom: 16 }}>BMI Reference Chart</div>
                <table className="bmi-table">
                  <thead>
                    <tr>
                      <th>BMI Range</th>
                      <th>Category</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BMI_CATS.map(c => {
                      const isMe = result.cat.label === c.label;
                      return (
                        <tr key={c.label} className={isMe ? 'highlight' : ''}>
                          <td>{c.range}</td>
                          <td style={{ color: c.color, fontWeight: 700 }}>
                            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c.color, marginRight: 8 }} />
                            {c.label}
                          </td>
                          <td>
                            {isMe
                              ? <span style={{ background: c.color, color: '#fff', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>You are here</span>
                              : <span style={{ color: '#444', fontSize: 12 }}>—</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ marginTop: 20, padding: '14px 16px', background: '#111', borderRadius: 10, fontSize: 12, color: '#666', lineHeight: 1.8 }}>
                  <strong style={{ color: '#999' }}>Formula used:</strong> Mifflin-St Jeor BMR &nbsp;·&nbsp;
                  Males: 10×W + 6.25×H − 5×A + 5 &nbsp;·&nbsp;
                  Females: 10×W + 6.25×H − 5×A − 161 &nbsp;·&nbsp;
                  TDEE = BMR × Activity Multiplier
                </div>
              </div>

              <button className="reset-btn" onClick={handleReset}>↩ Recalculate</button>

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WeightCalculator;
